import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAmpPlans } from "@/hooks/useAmpPlans";
import { AmpMainDocumentPanel } from "./AmpMainDocumentPanel";

interface Props {
  projectId: string;
  folderId?: string | null;
  /** Skapar/hittar en mapp när dokumentet skapas från projektnivå */
  ensureFolderId?: () => Promise<string | null>;
}

export const AmpDocumentsSection: React.FC<Props> = ({ projectId, folderId, ensureFolderId }) => {
  const navigate = useNavigate();
  const { plans, loading, createPlan, deletePlan } = useAmpPlans(projectId, folderId ?? null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<Record<string, { done: number; total: number }>>({});

  const planIds = useMemo(() => plans.map((p) => p.id), [plans]);

  useEffect(() => {
    if (planIds.length === 0) {
      setProgress({});
      return;
    }
    const load = async () => {
      const { data, error } = await supabase
        .from("amp_sections")
        .select("plan_id, status")
        .in("plan_id", planIds);
      if (error || !data) return;
      const next: Record<string, { done: number; total: number }> = {};
      for (const row of data as { plan_id: string | null; status: string }[]) {
        if (!row.plan_id) continue;
        const entry = next[row.plan_id] ?? { done: 0, total: 0 };
        entry.total += 1;
        if (row.status === "done" || row.status === "not_applicable") entry.done += 1;
        next[row.plan_id] = entry;
      }
      setProgress(next);
    };
    load();
  }, [planIds.join(",")]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const target = folderId || (ensureFolderId ? await ensureFolderId() : null);
      const id = await createPlan(name || "Arbetsmiljöplan", target);
      setName("");
      if (id) navigate(`/amp/${projectId}/${id}`);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  return (
    <AmpMainDocumentPanel
      plans={plans}
      progress={progress}
      name={name}
      onNameChange={setName}
      busy={busy}
      onCreate={handleCreate}
      onOpen={(planId) => navigate(`/amp/${projectId}/${planId}`)}
      onDelete={deletePlan}
    />
  );
};

export default AmpDocumentsSection;
